import React, { useState, useRef, useEffect, useMemo } from "react";
import { beepAndVibrate } from "../utils/feedback";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  Check,
  Copy,
  Info,
  Pencil,
  RotateCcw,
  ScanLine,
  Upload,
} from "lucide-react";
import { apiService } from "../services/apiService";
import { resolveNamaIbu } from "../utils/motherName";
import { getUploadErrorMessage } from "../utils/uploadError";
import mmsBottleImg from "../assets/mms-bottle.svg";
import toast from "react-hot-toast";

const CameraScreen = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [manualCount, setManualCount] = useState("");
  const [editingCount, setEditingCount] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState(50); // Default estimation
  const [countMethods, setCountMethods] = useState(null);
  const [confidence, setConfidence] = useState(() => {
    // Load confidence from localStorage or use default
    const savedConfidence = localStorage.getItem("pillDetectionConfidence");
    return savedConfidence ? parseFloat(savedConfidence) : 0.05;
  });
  const [autoAdjustConfidence, setAutoAdjustConfidence] = useState(() => {
    // Load auto-adjust setting from localStorage or use default (enabled)
    const savedAutoAdjust = localStorage.getItem("autoAdjustConfidence");
    return savedAutoAdjust ? JSON.parse(savedAutoAdjust) : true;
  });
  const [annotatedPreviewUrl, setAnnotatedPreviewUrl] = useState(null);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [nameMother, setNameMother] = useState(null);
  const [loadingMother, setLoadingMother] = useState(false);
  const [manualNameMother, setManualNameMother] = useState("");
  const [confirmedNameMother, setConfirmedNameMother] = useState("");
  const [idIbu, setIdIbu] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const motherInfo = location.state?.motherInfo;
  const barcodeId = scannedBarcode || motherInfo?.barcode_id;
  const namaIbu = resolveNamaIbu({
    nameMother,
    confirmedNameMother,
    manualNameMother,
  });

  useEffect(() => {
    if (location.state?.barcode) {
      setScannedBarcode(location.state.barcode);
      if (location.state.nameMother) {
        setConfirmedNameMother(location.state.nameMother);
      }
      if (location.state.idIbu) {
        setIdIbu(location.state.idIbu);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const fetchNameMother = async () => {
      if (!scannedBarcode) return;

      setLoadingMother(true);
      try {
        const scanData = await apiService.scanBarcode(scannedBarcode);
        if (scanData.name_mother) {
          setNameMother(scanData.name_mother);
        }
        if (scanData.id_ibu || scanData.respondent_id) {
          setIdIbu(scanData.id_ibu || scanData.respondent_id);
        }
      } catch (error) {
        console.error("Error fetching name_mother:", error);
      } finally {
        setLoadingMother(false);
      }
    };

    fetchNameMother();
  }, [scannedBarcode]);

  // Load count methods on component mount
  useEffect(() => {
    const loadCountMethods = async () => {
      try {
        const methods = await apiService.getCountMethods();
        setCountMethods(methods);
      } catch (error) {
        console.error("Failed to load count methods:", error);
      }
    };
    loadCountMethods();
  }, []);

  // Save confidence to localStorage whenever it changes
  const handleConfidenceChange = (newConfidence) => {
    setConfidence(newConfidence);
    localStorage.setItem("pillDetectionConfidence", newConfidence.toString());
  };

  // Handle auto-adjust confidence toggle
  const handleAutoAdjustToggle = (enabled) => {
    setAutoAdjustConfidence(enabled);
    localStorage.setItem("autoAdjustConfidence", enabled.toString());
  };

  const resetResult = () => {
    setAiResult(null);
    setManualCount("");
    setEditingCount(false);
    setConfirmed(false);
    setAnnotatedPreviewUrl(null);
  };

  const handleImageSelect = (event) => {
    const files = event.target.files || (event.dataTransfer && event.dataTransfer.files);
    const file = files && files[0];
    if (file) {
      if (!file.type || file.type.startsWith("image/") || /\.(jpg|jpeg|png|webp)$/i.test(file.name || "")) {
        setSelectedImage(file);
        let url = "";
        try {
          url = (typeof window !== "undefined" && window.URL && window.URL.createObjectURL)
            ? window.URL.createObjectURL(file)
            : URL.createObjectURL(file);
        } catch (e) {
          url = "blob:mock-image-url";
        }
        setPreviewUrl(url || "blob:mock-image-url");
        resetResult();
      } else {
        toast.error("Please select an image file");
      }
    }
  };

  const handleRetake = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    resetResult();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const normalizedBoundingBoxes = useMemo(() => {
    if (!Array.isArray(aiResult?.bounding_boxes)) return [];
    return aiResult.bounding_boxes
      .map((box) => {
        const rawBox =
          Array.isArray(box) && box.length === 4
            ? box
            : Array.isArray(box?.bbox) && box.bbox.length === 4
              ? box.bbox
              : null;
        if (!rawBox) return null;
        const [x1, y1, x2, y2] = rawBox;
        const left = Number(x1);
        const top = Number(y1);
        const right = Number(x2);
        const bottom = Number(y2);
        if (
          !Number.isFinite(left) ||
          !Number.isFinite(top) ||
          !Number.isFinite(right) ||
          !Number.isFinite(bottom)
        ) {
          return null;
        }
        return {
          left,
          top,
          width: Math.max(0, right - left),
          height: Math.max(0, bottom - top),
          cx: (left + right) / 2,
          cy: (top + bottom) / 2,
          label:
            typeof box?.label === "string" && box.label.trim()
              ? box.label.trim()
              : null,
          kind:
            typeof box?.kind === "string" && box.kind.trim()
              ? box.kind.trim()
              : "detection",
        };
      })
      .filter(Boolean);
  }, [aiResult?.bounding_boxes]);

  useEffect(() => {
    let cancelled = false;
    if (aiResult?.annotated_image_base64) {
      setAnnotatedPreviewUrl(
        `data:image/jpeg;base64,${aiResult.annotated_image_base64}`,
      );
      return () => {
        cancelled = true;
      };
    }
    if (!previewUrl || normalizedBoundingBoxes.length === 0) {
      setAnnotatedPreviewUrl(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 4;
      ctx.font = "20px sans-serif";

      normalizedBoundingBoxes.forEach((box) => {
        const strokeColor =
          box.kind === "fill_level"
            ? "#f59e0b"
            : box.kind === "bottle"
              ? "#06b6d4"
              : "#22c55e";
        ctx.strokeStyle = strokeColor;
        ctx.strokeRect(box.left, box.top, box.width, box.height);

        if (box.kind !== "fill_level") {
          ctx.fillStyle = "#ef4444";
          ctx.beginPath();
          ctx.arc(box.cx, box.cy, 6, 0, Math.PI * 2);
          ctx.fill();
        }

        if (box.label) {
          const pad = 6;
          const textW = ctx.measureText(box.label).width;
          const labelW = Math.max(60, textW + pad * 2);
          const labelH = 24;
          const labelX = Math.max(0, box.left);
          const labelY = Math.max(0, box.top - labelH - 2);
          ctx.fillStyle = "rgba(0,0,0,0.7)";
          ctx.fillRect(labelX, labelY, labelW, labelH);
          ctx.fillStyle = "#ffffff";
          ctx.fillText(box.label, labelX + pad, labelY + 17);
        }
      });

      setAnnotatedPreviewUrl(canvas.toDataURL("image/png"));
    };
    img.src = previewUrl;

    return () => {
      cancelled = true;
    };
  }, [previewUrl, normalizedBoundingBoxes, aiResult?.annotated_image_base64]);

  const analyzeImage = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setLoading(true);
    try {
      // Determine which confidence to use based on auto-adjust setting
      const confidenceToUse = autoAdjustConfidence ? 0.05 : confidence; // Use default when auto-adjust is enabled

      // Always use intelligent counting with estimation, passing barcodeId for ML dataset linkage
      const result = await apiService.countPillsWithEstimation(
        selectedImage,
        estimatedCount,
        confidenceToUse,
        barcodeId,
      );

      const methodUsed =
        estimatedCount <= 50 ? "YOLO Detection" : "Fill Height Estimation";
      const adjustmentInfo =
        autoAdjustConfidence && estimatedCount <= 50
          ? " with auto-adjustment"
          : "";

      beepAndVibrate();

      toast.success(`Analysis Completed using ${methodUsed}${adjustmentInfo}!`);
      setAiResult({ ...result, method: methodUsed });
      setManualCount(result.pill_count.toString());
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast.error(getUploadErrorMessage(error), { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage || !aiResult || manualCount === "") {
      toast.error("Please provide an image and pill count");
      return;
    }

    const barcodeIdForSubmit = barcodeId;
    if (!barcodeIdForSubmit) {
      toast.error("Barcode ID is required. Please scan barcode first.");
      return;
    }

    if (!namaIbu) {
      toast.error("Nama ibu diperlukan. Silakan masukkan nama ibu.");
      return;
    }

    const finalCount = parseInt(manualCount, 10);
    if (isNaN(finalCount) || finalCount <= 0) {
      toast.error("Please enter a valid pill count");
      return;
    }

    setSubmitting(true);
    try {
      const captureId = aiResult.capture_id || null;
      const imageUrl = aiResult.image_url || aiResult.gcs_uri || null;
      const yoloImage = aiResult.yolo_image || null;

      // jumlah_pills keeps the AI count; a manual correction goes to jumlah_pills_koreksi
      const result = await apiService.submitCounting({
        barcode_id: barcodeIdForSubmit,
        jumlah_pills: aiResult.pill_count,
        jumlah_pills_koreksi:
          finalCount !== aiResult.pill_count ? finalCount : null,
        detection_type: "mms_bottle",
        nama_ibu: namaIbu,
        id_ibu: idIbu || null,
        capture_id: captureId,
        ai_metadata: aiResult.ai_metadata || null,
        image_url: imageUrl,
        yolo_image: yoloImage,
      });

      toast.success("MMS pill count submitted successfully!");

      const imageAttachment = annotatedPreviewUrl || previewUrl;

      navigate("/results", {
        state: {
          record: {
            barcode_id: barcodeIdForSubmit,
            detection_type: "mms_bottle",
            jenis_counting: "MMS Botol",
            nama_ibu: namaIbu,
            id_ibu: idIbu || result?.id_ibu || null,
            capture_id: captureId,
            image_url: result?.image_url || imageUrl,
            yolo_image: result?.yolo_image || yoloImage,
            image: imageAttachment,
            ...result,
            jumlah_pills: finalCount,
          },
          motherInfo: motherInfo || { barcode_id: barcodeIdForSubmit },
          aiResult,
          capture_id: captureId,
          image: imageAttachment,
        },
      });
    } catch (error) {
      console.error("Error submitting record:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to submit record";
      toast.error(`Failed to submit record: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyBarcode = async () => {
    try {
      await navigator.clipboard.writeText(barcodeId);
      toast.success("Barcode copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy barcode");
    }
  };

  const saveManualNameMother = () => {
    if (manualNameMother.trim()) {
      setConfirmedNameMother(manualNameMother.trim());
      toast.success("Nama ibu tersimpan");
    }
  };

  const isCorrected =
    aiResult && manualCount !== "" && Number(manualCount) !== aiResult.pill_count;
  const isYolo = estimatedCount <= 50;

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate("/")}
              className="mr-4 p-2 text-gray-900 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white"
              aria-label="Kembali"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              MMS Botol Counting
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Mother & barcode info */}
        <dl className="px-1 space-y-1 text-gray-900 dark:text-gray-200">
          <div className="flex gap-2">
            <dt className="shrink-0">Nama Ibu :</dt>
            <dd className="break-words">
              {loadingMother ? (
                <span className="italic text-gray-500">Memuat...</span>
              ) : nameMother || confirmedNameMother ? (
                <>
                  {nameMother || confirmedNameMother}
                  {!nameMother && (
                    <button
                      onClick={() => {
                        setManualNameMother(confirmedNameMother);
                        setConfirmedNameMother("");
                      }}
                      className="ml-2 text-sm text-blue-600 underline"
                    >
                      Edit
                    </button>
                  )}
                </>
              ) : barcodeId ? (
                <span className="flex gap-2">
                  <input
                    type="text"
                    value={manualNameMother}
                    onChange={(e) => setManualNameMother(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveManualNameMother()}
                    placeholder="Masukkan nama ibu"
                    aria-label="Nama ibu"
                    className="min-w-0 flex-1 px-2 py-1 border border-slate-400 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-blue-600"
                  />
                  {manualNameMother.trim() && (
                    <button
                      onClick={saveManualNameMother}
                      className="px-3 py-1 rounded-md bg-blue-600 text-sm text-white hover:bg-blue-700"
                    >
                      Simpan
                    </button>
                  )}
                </span>
              ) : (
                "-"
              )}
            </dd>
          </div>
          {barcodeId && (
            <div className="flex items-center gap-2">
              <dt>ID MMS :</dt>
              <dd className="font-mono break-all">{barcodeId}</dd>
              <button
                onClick={copyBarcode}
                className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                aria-label="Copy barcode"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          )}
        </dl>

        {/* Image card */}
        <div className="mt-8 rounded-xl bg-white dark:bg-gray-800 shadow-md px-6 py-8">
          {!previewUrl ? (
            // State 1: no image yet
            <div className="flex flex-col items-center text-center">
              <img src={mmsBottleImg} alt="" className="h-36 w-auto" />
              <h2 className="mt-8 text-lg font-bold text-gray-900 dark:text-white">
                Add MMS Botol Image
              </h2>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-300">
                Pastikan gambar MMS Botol terlihat jelas
              </p>
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="mt-8 w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700"
              >
                <Camera className="h-6 w-6" />
                Ambil Foto
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-lg text-gray-900 dark:text-gray-100 hover:bg-gray-300"
              >
                <Upload className="h-6 w-6" />
                Pilih dari Galeri
              </button>
              {/* Android Chrome often opens only the gallery without capture, so camera gets its own input */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageSelect}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="image-input"
              />
            </div>
          ) : (
            <>
              <div className="relative mx-auto w-fit overflow-hidden rounded-lg">
                <img
                  src={aiResult ? annotatedPreviewUrl || previewUrl : previewUrl}
                  alt={aiResult ? "Hasil deteksi botol" : "Pill bottle"}
                  className="max-h-80 w-auto object-contain"
                />
                {loading && (
                  <div
                    role="status"
                    aria-label="Menganalisis gambar"
                    className="ai-scan-shimmer absolute inset-0"
                  >
                    <div className="animate-scan-laser" />
                  </div>
                )}
              </div>

              {!aiResult ? (
                // State 2: image added, not analyzed yet
                <div className="mt-8 space-y-4">
                  <button
                    onClick={handleRetake}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-lg text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                  >
                    <RotateCcw className="h-6 w-6" />
                    Ambil Ulang
                  </button>
                  <button
                    onClick={analyzeImage}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700 disabled:opacity-70"
                  >
                    <ScanLine className="h-6 w-6" />
                    {loading ? "Analyzing..." : "Analyze"}
                  </button>
                </div>
              ) : (
                // State 3: analyzed
                <>
                  <div className="mt-10 flex items-center justify-between gap-4 px-2">
                    <div>
                      <p className="text-gray-900 dark:text-gray-200">
                        Hasil Counting
                      </p>
                      {editingCount ? (
                        <input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          value={manualCount}
                          onChange={(e) => {
                            setManualCount(e.target.value);
                            setConfirmed(false);
                          }}
                          aria-label="Koreksi jumlah pil"
                          className="mt-1 w-28 px-3 py-1 border border-slate-400 rounded-md bg-white dark:bg-gray-900 text-xl font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-600"
                          autoFocus
                        />
                      ) : (
                        <p className="text-2xl font-bold text-green-600">
                          {manualCount} Pil
                        </p>
                      )}
                      {isCorrected && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Hasil AI: {aiResult.pill_count} Pil
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Metode: {aiResult.method}
                        {aiResult.dynamic_confidence
                          ? ` · Confidence ${Math.round(aiResult.dynamic_confidence * 100)}%`
                          : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => setEditingCount((v) => !v)}
                      disabled={editingCount && manualCount === ""}
                      className="flex shrink-0 items-center gap-2 px-5 py-3 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                    >
                      {editingCount ? (
                        <>
                          <Check className="h-5 w-5" />
                          Selesai
                        </>
                      ) : (
                        <>
                          <Pencil className="h-5 w-5" />
                          Koreksi
                        </>
                      )}
                    </button>
                  </div>
                  {/* Wrong image already analyzed: start over and analyze again */}
                  <button
                    onClick={handleRetake}
                    disabled={submitting}
                    className="mt-6 w-full flex items-center justify-center gap-3 py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                  >
                    <RotateCcw className="h-5 w-5" />
                    Ambil Ulang
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {!aiResult ? (
          <>
            {/* Estimated Pill Count */}
            <div className="mt-10 rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <label
                  htmlFor="estimatedCount"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Estimated Pill Count: {estimatedCount}
                </label>
                <span
                  className={`shrink-0 text-xs px-2 py-1 rounded-full ${
                    isYolo
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                      : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                  }`}
                >
                  {isYolo ? "YOLO Detection" : "Fill Height Estimation"}
                </span>
              </div>
              <input
                type="range"
                id="estimatedCount"
                min="1"
                max="180"
                value={estimatedCount}
                onChange={(e) => setEstimatedCount(parseInt(e.target.value))}
                disabled={loading}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, ${
                    isYolo ? "#dbeafe 0%, #3b82f6" : "#dcfce7 0%, #22c55e"
                  } ${(estimatedCount / 180) * 100}%, #e5e7eb ${
                    (estimatedCount / 180) * 100
                  }%, #e5e7eb 100%)`,
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>1</span>
                <span>180</span>
              </div>
              {countMethods && (
                <p className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                  {isYolo
                    ? "Best for individual pill detection with clear visibility"
                    : "Best for bulk estimation using bottle fill height"}
                </p>
              )}
            </div>

            {/* AI Detection Settings - only for YOLO */}
            {isYolo && (
              <div className="relative mt-6 rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    AI Detection Settings
                  </h3>
                  {autoAdjustConfidence && (
                    <>
                      <button
                        type="button"
                        className="peer/info shrink-0 rounded-full p-0.5 text-purple-600 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-purple-400"
                        aria-label="Auto-adjustment information"
                        aria-describedby="auto-adjust-info-tooltip"
                      >
                        <Info className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <div
                        id="auto-adjust-info-tooltip"
                        role="tooltip"
                        className="pointer-events-none invisible absolute inset-x-4 top-12 z-20 rounded-lg border border-purple-200 bg-white p-3 text-xs opacity-0 shadow-lg transition-opacity peer-hover/info:visible peer-hover/info:opacity-100 peer-focus-visible/info:visible peer-focus-visible/info:opacity-100 dark:border-purple-700 dark:bg-gray-800"
                      >
                        <strong className="mb-1 block text-purple-800 dark:text-purple-200">
                          Auto-Adjustment Active:
                        </strong>
                        <p className="mb-2 text-purple-600 dark:text-purple-400">
                          Confidence threshold will be automatically optimized
                          based on your pill count estimation.
                        </p>
                        <ul className="space-y-0.5 text-purple-600 dark:text-purple-400">
                          <li>• 1-10 pills: Advance confidence (90%)</li>
                          <li>• 11-20 pills: High confidence (50%-80%)</li>
                          <li>• 21-30 pills: Normal confidence (21%-49%)</li>
                          <li>• 31-50 pills: Low confidence (5%-20%)</li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                {/* Auto-Adjust Toggle */}
                <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="min-w-0">
                    <p
                      id="auto-adjust-label"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Auto-adjust confidence
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Optimize threshold based on your pill count estimation
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoAdjustConfidence}
                    aria-labelledby="auto-adjust-label"
                    onClick={() => handleAutoAdjustToggle(!autoAdjustConfidence)}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-700 ${
                      autoAdjustConfidence
                        ? "bg-blue-600"
                        : "bg-gray-300 dark:bg-gray-500"
                    }`}
                  >
                    <span
                      className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        autoAdjustConfidence ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Manual Confidence Settings - only when auto-adjust is off */}
                {!autoAdjustConfidence && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <label
                        htmlFor="confidence"
                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Confidence Threshold: {(confidence * 100).toFixed(0)}%
                      </label>
                      <span
                        className={`shrink-0 text-xs px-2 py-1 rounded-full ${
                          confidence <= 0.1
                            ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                            : confidence <= 0.3
                              ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                              : "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                        }`}
                      >
                        {confidence <= 0.1
                          ? "Sensitive"
                          : confidence <= 0.3
                            ? "Balanced"
                            : "Strict"}
                      </span>
                    </div>
                    <div className="relative">
                      <div className="confidence-indicator w-full"></div>
                      <input
                        type="range"
                        id="confidence"
                        min="0.01"
                        max="1.0"
                        step="0.01"
                        value={confidence}
                        onChange={(e) =>
                          handleConfidenceChange(parseFloat(e.target.value))
                        }
                        className="w-full h-2 bg-transparent rounded-lg appearance-none cursor-pointer slider"
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>1% (More)</span>
                      <span>100% (Fewer)</span>
                    </div>

                    {/* Quick presets as a segmented control */}
                    <div className="grid grid-cols-3 gap-1 rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
                      {[
                        [0.05, "5% Default"],
                        [0.25, "25% Balanced"],
                        [0.5, "50% Strict"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={confidence === value}
                          onClick={() => handleConfidenceChange(value)}
                          className={`py-1.5 text-xs rounded-md transition-colors ${
                            confidence === value
                              ? "bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-medium shadow-sm"
                              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Instructions */}
            <div className="mt-10 rounded-lg border border-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-800 px-6 py-5 text-cyan-900 dark:text-cyan-200">
              <h3 className="text-lg font-bold">Tata cara pengambilan gambar</h3>
              <ul className="mt-3 list-disc pl-5 space-y-1">
                <li>Pastikan menggunakan gambar dengan fokus jelas dan tidak blur</li>
                <li>Pastikan seluruh botol terlihat dalam gambar</li>
                <li>Gunakan flash jika terlalu gelap dan minim cahaya</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <label className="mt-12 flex items-center gap-3 px-1 text-gray-900 dark:text-gray-200">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={editingCount}
                className="h-5 w-5 rounded border-slate-400"
              />
              Saya menyatakan hasil counting sudah benar
            </label>

            <button
              onClick={handleSubmit}
              disabled={!confirmed || editingCount || submitting}
              className="mt-6 w-full py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700 disabled:bg-blue-600/30 disabled:cursor-not-allowed"
            >
              {submitting ? "Menyimpan..." : "Simpan"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraScreen;
